/**
 * 인사 홈의 «처리 대기» — 기한이 있는 일을 한 자리에 모은다 (2026-08-27).
 *
 * 인사기록 다섯 화면(상벌 · 평가 · 교육/자격 · 정정 요청)과 인사발령을 만들어 놓고 보니,
 * 담당자가 **매일 그 화면을 하나씩 열어 봐야만** 처리할 것을 알 수 있었다. 홈이 「오늘 처리할
 * 것」 이라고 하면서 그것들을 모르는 상태였다.
 *
 * ★★ **새 표도 새 서버 라우트도 없다.** 담당자가 이미 부르는 응답들의 `summary` 를 모을 뿐이다
 *    (A18 인사 통계와 같은 모양).
 * ★★ **못 읽은 조각은 «—» 이지 «0» 이 아니다.** 0 으로 채우면 「처리할 것이 없다」 는
 *    거짓말이 된다 — 서버가 아직 그 라우트를 모를 수도, 권한이 없을 수도 있다.
 * ★ **0 인 줄을 지우지 않는다.** 지우면 「그 일이 없는지」 와 「아직 안 읽었는지」 를 구분할 수
 *   없다. 대신 **급한 것을 위로** 올리고 0 은 흐리게 아래에 둔다.
 */

/** 집계에 쓰는 값 — `null` 은 **못 읽었다**(0 이 아니다). */
export type Count = number | null;

export interface PendingInput {
  /** 정정 요청 — 답을 기다리는 것과, 그중 발령이 필요한 것. */
  requests: { pending: Count; needsAssignment: Count };
  /** 시행일이 지났는데 아직 안 누른 발령. */
  assignmentsOverdue: Count;
  /** 법정교육을 하나라도 안 들은 사람 — **과태료 대상**이다. */
  courseNotCompleted: Count;
  /** 자격 · 어학 — 창 안에 만료되는 것과 이미 지난 것. */
  credentials: { expiringSoon: Count; expired: Count };
  /** 징계 말소 예정(창 안). */
  awardsExpiringSoon: Count;
  /** 진행 중인 평가 회차 수. */
  activeRounds: Count;
  /**
   * ★★ **계정만 있고 인사 정보가 없는 사람** (2026-08-28 · 자리 재정의 판 4).
   *
   *    사용자 관리가 «계정만» 다루기로 하면서(#758) 계정을 만든 뒤 **인사가 이어받는다** 는
   *    고리가 생겼다. 그 고리의 끝이 여기다 — 이 줄이 없으면 계정은 만들어지는데
   *    **아무도 이어받지 않는다**(누가 넘어왔는지 알 길이 없다).
   */
  unfinished: UnfinishedProfiles;
}

/** 무엇이 비었는가 — 사람 수와, 칸별로 몇 명인지. */
export interface UnfinishedProfiles {
  /** 셋 중 **하나라도** 빈 사람 수. */
  total: Count;
  noTeam: Count;
  noHireDate: Count;
  noPosition: Count;
}

/** 세는 데 필요한 칸만 — API 타입에 묶지 않는다(집계는 여기 하나뿐이라는 규율). */
export interface EmployeeLike {
  employmentStatus: string;
  teamId: string | null;
  hireDate: string | null;
  position: string | null;
}

/**
 * 재직자 중 인사 정보가 덜 채워진 사람을 센다.
 *
 * ★ **재직자만** — 퇴사·휴직자의 빈 칸은 이제 와서 채울 것이 아니다(인사 통계와 같은 규칙).
 * ★ 못 읽었으면(`null`) 전부 `null` — 0 으로 채우면 「다 채워졌다」 는 거짓말이 된다.
 */
export function unfinishedProfiles(rows: readonly EmployeeLike[] | null): UnfinishedProfiles {
  if (rows === null) return { total: null, noTeam: null, noHireDate: null, noPosition: null };
  const active = rows.filter((r) => r.employmentStatus === 'ACTIVE');
  const noTeam = active.filter((r) => !r.teamId);
  const noHireDate = active.filter((r) => !r.hireDate);
  const noPosition = active.filter((r) => !r.position);
  return {
    total: active.filter((r) => !r.teamId || !r.hireDate || !r.position).length,
    noTeam: noTeam.length,
    noHireDate: noHireDate.length,
    noPosition: noPosition.length,
  };
}

export interface PendingRow {
  key: string;
  label: string;
  /** 왜 급한가 — 한 줄. 숫자가 0 이면 화면이 이 문구를 흐리게 쓴다. */
  why: string;
  count: Count;
  unit: string;
  to: string;
  linkLabel: string;
  /** 곁들이는 말 — 「그중 발령 필요 2건」 처럼. 없으면 null. */
  extra: string | null;
  /**
   * 눈에 띄어야 하나 — **`0` 도 `null` 도 아니어야** 한다.
   * 「못 읽음(—)」 은 급한 것이 아니라 **모르는 것**이라, 경고로 칠하지 않는다.
   */
  urgent: boolean;
}

const has = (n: Count): n is number => typeof n === 'number' && n > 0;

/** 「소속 3 · 입사일 5 · 직위 2」 — 0 인 칸은 빼고, 셋 다 0/못 읽음이면 없음. */
function unfinishedExtra(u: UnfinishedProfiles): string | null {
  const parts: string[] = [];
  if (has(u.noTeam)) parts.push(`소속 ${u.noTeam}`);
  if (has(u.noHireDate)) parts.push(`입사일 ${u.noHireDate}`);
  if (has(u.noPosition)) parts.push(`직위 ${u.noPosition}`);
  return parts.length === 0 ? null : parts.join(' · ');
}

/**
 * 화면이 그릴 줄 목록 — **급한 것이 먼저**, 그다음 0, 못 읽은 것은 맨 뒤.
 *
 * ★ 「법정교육 미이수」 를 맨 앞 후보로 둔다(같은 급이면). 과태료가 걸린 유일한 항목이라
 *   같은 «1건» 이라도 무게가 다르다.
 */
export function pendingRows(input: PendingInput): PendingRow[] {
  const u = input.unfinished;
  const rows: PendingRow[] = [
    {
      key: 'unfinished',
      label: '인사 정보 없는 계정',
      why: '부서 · 직위 · 입사일이 있어야 근태와 연차가 돕니다',
      count: u.total,
      unit: '명',
      to: '/erp/hr/employees',
      linkLabel: '직원 관리',
      // 무엇이 비었는지 적는다 — 「3명」 만으로는 어디를 열어야 할지 모른다.
      extra: unfinishedExtra(u),
      urgent: has(u.total),
    },
    {
      key: 'courses',
      label: '법정교육 미이수',
      why: '미이수는 과태료 대상입니다',
      count: input.courseNotCompleted,
      unit: '명',
      to: '/erp/hr/records/training',
      linkLabel: '교육 · 자격',
      extra: null,
      urgent: has(input.courseNotCompleted),
    },
    {
      key: 'requests',
      label: '정정 요청 대기',
      why: '직원이 답을 기다립니다',
      count: input.requests.pending,
      unit: '건',
      to: '/erp/hr/profile-requests',
      linkLabel: '정정 요청',
      extra: has(input.requests.needsAssignment)
        ? `그중 발령이 필요한 것 ${input.requests.needsAssignment}건`
        : null,
      urgent: has(input.requests.pending),
    },
    {
      key: 'assignments',
      label: '시행일이 지난 발령',
      why: '누르지 않으면 명부가 현실과 어긋난 채로 남습니다',
      count: input.assignmentsOverdue,
      unit: '건',
      to: '/erp/hr/assignments',
      linkLabel: '인사발령',
      extra: null,
      urgent: has(input.assignmentsOverdue),
    },
    {
      key: 'credentials',
      label: '자격 · 어학 만료 임박',
      why: '만료되면 증명서에서 빠집니다',
      count: input.credentials.expiringSoon,
      unit: '건',
      to: '/erp/hr/records/training',
      linkLabel: '교육 · 자격',
      extra: has(input.credentials.expired) ? `이미 지난 것 ${input.credentials.expired}건` : null,
      // ★ 이미 지난 것이 있으면 임박이 0 이라도 급하다 — 증명서에서 이미 빠지고 있다.
      urgent: has(input.credentials.expiringSoon) || has(input.credentials.expired),
    },
    {
      key: 'awards',
      label: '징계 말소 예정',
      why: '말소된 기록은 더는 불이익에 쓰지 않습니다',
      count: input.awardsExpiringSoon,
      unit: '건',
      to: '/erp/hr/records/awards',
      linkLabel: '포상 · 징계',
      extra: null,
      urgent: has(input.awardsExpiringSoon),
    },
    {
      key: 'rounds',
      label: '진행 중인 평가 회차',
      why: '회차를 열면 대상자의 제출을 지켜봐야 합니다',
      count: input.activeRounds,
      unit: '개',
      to: '/erp/hr/evaluations',
      linkLabel: '인사평가',
      extra: null,
      urgent: false,   // 진행 중인 것은 «일» 이지 «밀린 일» 이 아니다.
    },
  ];

  // 급한 것 → 아는데 0 인 것 → 못 읽은 것. 같은 급이면 원래 차례(위 목록 순서)를 지킨다.
  const rank = (r: PendingRow) => (r.urgent ? 0 : r.count === null ? 2 : 1);
  return rows
    .map((r, i) => ({ r, i }))
    .sort((a, b) => rank(a.r) - rank(b.r) || a.i - b.i)
    .map(({ r }) => r);
}

/** 머리에 적을 한 줄 — 급한 줄이 몇인가. 하나도 못 읽었으면 «—». */
export function pendingHeadline(rows: readonly PendingRow[]): string {
  const known = rows.filter((r) => r.count !== null);
  if (known.length === 0) return '아직 읽지 못했습니다';
  const urgent = rows.filter((r) => r.urgent).length;
  return urgent > 0 ? `${urgent}가지가 기다립니다` : '기다리는 일이 없습니다';
}

/** 숫자 칸에 쓸 글자 — **못 읽은 것은 «—»**(0 이 아니다). */
export function countText(count: Count, unit: string): string {
  return count === null ? '—' : `${count}${unit}`;
}
